import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

// The backend Socket.IO server lives on the API origin (not the admin origin),
// so we connect directly rather than through the /api proxy. Override with
// VITE_SOCKET_URL for local backend testing (e.g. http://localhost:3311).
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'https://api.zooma.ng';

let socket = null;

/**
 * Connect (once) to the realtime gateway using the stored admin token. The
 * gateway reads the JWT from the handshake auth and joins this client to its
 * `role:<role>` room, so super admins automatically receive `admin:*` events.
 */
export function connectAdminSocket() {
  const token = localStorage.getItem('token');
  if (!token) return null;
  const cleanToken = token.startsWith('Bearer ') ? token.slice(7) : token;

  if (socket && socket.connected) return socket;
  if (socket) {
    // Refresh auth in case the token changed, then reconnect.
    socket.auth = { token: cleanToken };
    socket.connect();
    return socket;
  }

  socket = io(`${SOCKET_URL}/realtime`, {
    auth: { token: cleanToken },
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 2000,
  });

  return socket;
}

export function getAdminSocket() {
  return socket;
}

export function disconnectAdminSocket() {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
}

/**
 * Calls `onChange()` when a rider's details change (pushed by the backend as
 * `admin:rider_updated`) or when this tab comes back into view — phones pause
 * the socket in the background, so a push can be missed. Pass `userId` to only
 * react to that rider (`null` while it's still loading); omit it for list screens.
 */
export function useRiderUpdates(onChange, userId) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const socket = connectAdminSocket();
    const onRiderUpdated = (data) => {
      if (userId === null) return;
      if (userId && String(data?.userId) !== String(userId)) return;
      onChangeRef.current();
    };
    const onVisible = () => {
      if (!document.hidden) onChangeRef.current();
    };
    socket?.on('admin:rider_updated', onRiderUpdated);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      socket?.off('admin:rider_updated', onRiderUpdated);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [userId]);
}
